import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useGetAllOrderShipments, useMarkOrderFulfilled, useGetDeliveryRoute } from '../hooks/backend/scanPage'
import { useGetTrackingEmailSettings } from '../hooks/backend/settingsPage'
import { useGetAllOrderLinesCached } from '../hooks/cachedQueries'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table'
import { Button } from '../lib/shadcn/button'
import { Checkbox } from '../lib/shadcn/checkbox'
import { Badge } from '../lib/shadcn/badge'
import { Input } from '../lib/shadcn/input'
import { Label } from '../lib/shadcn/label'
import { Switch } from '../lib/shadcn/switch'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../lib/shadcn/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../lib/shadcn/alert-dialog'
import { cn } from '../lib/shadcn/utils'
import { RefreshCw, Filter, Package, Truck, CheckCircle, Loader2, FileText } from 'lucide-react'
import { toast } from '../lib/shadcn/sonner'
import { fulfillmentSyncWarning } from '../utils/syncResults'
import ShipOrderDialog from './ui/ShipOrderDialog'
import DeliverOrderDialog from './ui/DeliverOrderDialog'
import BulkDeliverDialog, { type BulkDeliverOrder } from './ui/BulkDeliverDialog'
import TrackingEmailDialog from './ui/TrackingEmailDialog'
import OrderLabelsMenu, { type OrderShipment } from './ui/OrderLabelsMenu'
import { generateDeliveryPdf, type DeliveryRow } from '../utils/deliveryPdf'
import { useShipFromAddress } from '../utils/shippingSettings'

// ─── Types ────────────────────────────────────────────────────────────────────

type DeliveryMethod = 'shipping' | 'delivery' | 'free_delivery'

interface OrderLine {
  id?: string | number
  order_id: string
  quantity_ordered: number
  quantity_picked: number
  customer?: string
  order_status?: string | null
  delivery_method?: string | null
}

interface FulfillmentOrder {
  order_id: string
  customer: string
  total_items: number
  quantity_picked: number
  delivery_method: DeliveryMethod
  status: string
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function normalizeMethod(method: string | null | undefined): DeliveryMethod {
  return method === 'delivery' || method === 'free_delivery' ? method : 'shipping'
}

function computeOrders(lines: OrderLine[]): FulfillmentOrder[] {
  const map = new Map<
    string,
    { total: number; picked: number; customer: string; method: DeliveryMethod; status: string }
  >()

  for (const line of lines) {
    const key = String(line.order_id)
    const entry = map.get(key) ?? {
      total: 0,
      picked: 0,
      customer: line.customer ?? '',
      method: normalizeMethod(line.delivery_method),
      status: String(line.order_status ?? '').toLowerCase(),
    }
    entry.total += Number(line.quantity_ordered ?? 0)
    entry.picked += Number(line.quantity_picked ?? 0)
    map.set(key, entry)
  }

  return Array.from(map.entries()).map(([order_id, { total, picked, customer, method, status }]) => ({
    order_id,
    customer,
    total_items: total,
    quantity_picked: picked,
    delivery_method: method,
    status,
  }))
}

function methodLabel(method: DeliveryMethod): string {
  return method === 'free_delivery' ? 'Free Delivery' : method === 'delivery' ? 'Delivery' : 'Shipping'
}

// ─── Reusable table ─────────────────────────────────────────────────────────

function OrdersTable({
  orders,
  columns,
  loading,
  filterValue,
  onFilterChange,
  onRefresh,
  refreshing,
  rowSelection,
  onRowSelectionChange,
  canSelectRow,
}: {
  orders: FulfillmentOrder[]
  columns: ColumnDef<FulfillmentOrder>[]
  loading: boolean
  filterValue: string
  onFilterChange: (value: string) => void
  onRefresh: () => void
  refreshing: boolean
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (next: RowSelectionState) => void
  canSelectRow?: (order: FulfillmentOrder) => boolean
}) {
  const [sorting, setSorting] = useState<SortingState>([])

  const table = useReactTable({
    data: orders,
    columns,
    state: { sorting, globalFilter: filterValue, rowSelection: rowSelection ?? {} },
    getRowId: row => row.order_id,
    enableRowSelection: row => (rowSelection ? (canSelectRow?.(row.original) ?? true) : false),
    onRowSelectionChange: updater => {
      if (!onRowSelectionChange) return
      const current = rowSelection ?? {}
      onRowSelectionChange(typeof updater === 'function' ? updater(current) : updater)
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: onFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <div className="relative">
          <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-8 text-sm w-48"
            placeholder="Filter..."
            value={filterValue}
            onChange={e => onFilterChange(e.target.value)}
          />
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} aria-label="Refresh">
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(hg => (
              <TableRow key={hg.id}>
                {hg.headers.map(header => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'whitespace-nowrap',
                      header.column.getCanSort() && 'cursor-pointer select-none'
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === 'asc'
                      ? ' ↑'
                      : header.column.getIsSorted() === 'desc'
                        ? ' ↓'
                        : ''}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-12">
                  {loading ? 'Loading…' : 'No orders found'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map(row => (
                <TableRow key={row.id} className="hover:bg-accent/50 transition-colors">
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id} className="whitespace-nowrap">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">{table.getFilteredRowModel().rows.length} order(s)</p>
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function ShippingPage() {
  const getAllOrderLines = useGetAllOrderLinesCached()
  const getAllOrderShipments = useGetAllOrderShipments()
  const markOrderFulfilled = useMarkOrderFulfilled()
  const getDeliveryRoute = useGetDeliveryRoute()
  const getTrackingEmailSettings = useGetTrackingEmailSettings()
  const [shipFromAddress] = useShipFromAddress()

  const [shippingFilter, setShippingFilter] = useState('')
  const [deliveryFilter, setDeliveryFilter] = useState('')
  const [showCompleted, setShowCompleted] = useState(false)
  // The Dashboard's Ready to deliver tile links to ?tab=delivery.
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('tab') === 'delivery' ? 'delivery' : 'shipping'
  const [orderToShip, setOrderToShip] = useState<string | null>(null)
  const [orderToDeliver, setOrderToDeliver] = useState<{ id: string; customer: string } | null>(null)
  const [deliverySelection, setDeliverySelection] = useState<RowSelectionState>({})
  const [bulkDeliverOrders, setBulkDeliverOrders] = useState<BulkDeliverOrder[] | null>(null)
  const [orderToMarkShipped, setOrderToMarkShipped] = useState<string | null>(null)
  const [markingShipped, setMarkingShipped] = useState(false)
  const [deliveryPdfLoading, setDeliveryPdfLoading] = useState(false)
  const [emailOrderId, setEmailOrderId] = useState<string | null>(null)
  const [emailOpen, setEmailOpen] = useState(false)
  const [pendingEmailOrderId, setPendingEmailOrderId] = useState<string | null>(null)
  const [trackingEmailEnabled, setTrackingEmailEnabled] = useState(false)

  const refreshAll = useCallback(() => {
    void getAllOrderLines.trigger(undefined, { skipCache: true })
    void getAllOrderShipments.trigger(undefined, { skipCache: true })
  }, [getAllOrderLines, getAllOrderShipments])

  useEffect(() => {
    getAllOrderLines.trigger()
    getAllOrderShipments.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    void (async () => {
      const res = (await getTrackingEmailSettings.trigger()) as { enabled: boolean } | undefined
      setTrackingEmailEnabled(res?.enabled ?? false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
  if (orderToShip !== null || !pendingEmailOrderId) return

  const orderId = pendingEmailOrderId
  setPendingEmailOrderId(null)

  requestAnimationFrame(() => {
    setEmailOrderId(orderId)
    setEmailOpen(true)
  })
}, [orderToShip, pendingEmailOrderId])

  const openTrackingEmail = useCallback((orderId: string) => {
    setEmailOrderId(orderId)
    setEmailOpen(true)
  }, [])

  const handleShipDialogEmailTracking = useCallback((orderId: string) => {
  setPendingEmailOrderId(orderId)
  setOrderToShip(null)
}, [])

  const allLines = useMemo<OrderLine[]>(
    () => (getAllOrderLines.data as OrderLine[] | undefined) ?? [],
    [getAllOrderLines.data]
  )

  const orders = useMemo<FulfillmentOrder[]>(() => computeOrders(allLines), [allLines])

  const shipmentsByOrder = useMemo<Map<string, OrderShipment[]>>(() => {
    const rows = (getAllOrderShipments.data as OrderShipment[] | undefined) ?? []
    const map = new Map<string, OrderShipment[]>()
    for (const row of rows) {
      const key = String(row.order_id)
      const list = map.get(key) ?? [] 
      list.push(row)
      map.set(key, list)
    }
    return map
  }, [getAllOrderShipments.data])

  const allShippingOrders = useMemo(
    () => orders.filter(o => o.delivery_method === 'shipping'),
    [orders]
  )
  const allDeliveryOrders = useMemo(
    () => orders.filter(o => o.delivery_method !== 'shipping'),
    [orders]
  )

  const isShipped = useCallback(
  (o: FulfillmentOrder) =>
    o.status === 'shipped',
  []
)

  // Tables show only orders still awaiting fulfillment, unless the user opts
  // to also show already shipped/delivered orders.
  const shippingOrders = useMemo(
    () => (showCompleted ? allShippingOrders : allShippingOrders.filter(o => !isShipped(o))),
    [allShippingOrders, isShipped, showCompleted]
  )
  // Delivery shows only fully picked orders (ready to load), plus delivered
  // ones when Show completed is on.
  const deliveryOrders = useMemo(
    () =>
      allDeliveryOrders.filter(o =>
        o.status === 'delivered'
          ? showCompleted
          : o.total_items > 0 && o.quantity_picked >= o.total_items
      ),
    [allDeliveryOrders, showCompleted]
  )

  // Only awaiting orders that are on screen count, so hiding or delivering an
  // order drops it from the bulk action.
  const selectedDeliveryOrders = useMemo(
    () => deliveryOrders.filter(o => deliverySelection[o.order_id] && o.status !== 'delivered'),
    [deliveryOrders, deliverySelection]
  )

  const handleBulkDelivered = useCallback(
    (deliveredIds: string[]) => {
      if (deliveredIds.length > 0) {
        setDeliverySelection(prev => {
          const next = { ...prev }
          for (const id of deliveredIds) delete next[id]
          return next
        })
      }
      refreshAll()
    },
    [refreshAll]
  )

  const handleOrderShipped = useCallback(() => {
    refreshAll()
  }, [refreshAll])
  const handleGenerateDeliveryPdf = useCallback(async () => {
    setDeliveryPdfLoading(true)
    try {
      const rows = (await getDeliveryRoute.trigger(
        { shipFrom: shipFromAddress },
        { skipCache: true }
      )) as DeliveryRow[]
      await generateDeliveryPdf(rows ?? [])
      toast.success('Delivery manifest opened')
    } catch (_e) {
      toast.error('Failed to generate delivery PDF')
    } finally {
      setDeliveryPdfLoading(false)
    }
  }, [getDeliveryRoute, shipFromAddress])

  const handleMarkShipped = useCallback(async () => {
    if (orderToMarkShipped === null) return
    setMarkingShipped(true)
    try {
      const result = await markOrderFulfilled.trigger({ order_id: orderToMarkShipped, status: 'shipped' })
      toast.success(`Order ${orderToMarkShipped} marked shipped`)
      const warning = fulfillmentSyncWarning((result as { sync_results?: unknown } | undefined)?.sync_results)
      if (warning) toast.warning(warning)
      refreshAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to mark shipped')
    } finally {
      setMarkingShipped(false)
      setOrderToMarkShipped(null)
    }
  }, [orderToMarkShipped, markOrderFulfilled, refreshAll])

  const stats = useMemo(() => {
    const shipped = allShippingOrders.filter(isShipped).length
    const delivered = allDeliveryOrders.filter(o => o.status === 'delivered').length
    return {
      shippingTotal: allShippingOrders.length,
      shipped,
      shippingAwaiting: allShippingOrders.length - shipped,
      deliveryTotal: allDeliveryOrders.length,
      delivered,
      // Same rule as the Delivery table: only fully picked orders are ready.
      deliveryAwaiting: allDeliveryOrders.filter(
        o => o.status !== 'delivered' && o.total_items > 0 && o.quantity_picked >= o.total_items
      ).length,
    }
  }, [allShippingOrders, allDeliveryOrders, isShipped])

  // ── Shared column builders ──
  const baseColumns: ColumnDef<FulfillmentOrder>[] = useMemo(
    () => [
      {
        accessorKey: 'order_id',
        header: 'Order ID',
        cell: ({ row }) => (
          <span className="font-mono font-medium text-foreground">{row.original.order_id}</span>
        ),
      },
      {
        accessorKey: 'customer',
        header: 'Company',
        cell: ({ row }) => <span className="text-foreground">{row.original.customer || '—'}</span>,
      },
      { accessorKey: 'total_items', header: 'Total Items' },
      { accessorKey: 'quantity_picked', header: 'Picked' },
    ],
    []
  )

  const shippingColumns = useMemo<ColumnDef<FulfillmentOrder>[]>(
    () => [
      ...baseColumns,
      {
  id: 'shipStatus',
  header: 'Shipment',
  cell: ({ row }) => {
    const shipments =
      shipmentsByOrder.get(
        row.original.order_id
      ) ?? []

    if (
      row.original.status ===
      'shipped'
    ) {
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-950">
          Shipped
        </Badge>
      )
    }

    if (shipments.length > 0) {
      return (
        <Badge variant="outline">
          Partially Shipped
        </Badge>
      )
    }

    return (
      <Badge variant="outline">
        Awaiting
      </Badge>
    )
  },
},
      {
  id: 'action',
  header: '',
  enableSorting: false,

  cell: ({ row }) => {
    const shipments =
      shipmentsByOrder.get(
        row.original.order_id
      ) ?? []

    const shipped =
      row.original.status ===
      'shipped'

    /*
     * Closed/shipped order:
     * labels can still be viewed,
     * but no more shipments can
     * be created.
     */
    if (shipped) {
      return (
        <div className="flex justify-end">
          {shipments.length > 0 && (
            <OrderLabelsMenu
              orderId={
                row.original.order_id
              }
              shipments={
                shipments
              }
              onRefreshed={() =>
                void getAllOrderShipments.trigger(
                  undefined,
                  {
                    skipCache: true,
                  }
                )
              }
              {...(
                trackingEmailEnabled
                  ? {
                      onEmailTracking:
                        () =>
                          openTrackingEmail(
                            row.original
                              .order_id
                          ),
                    }
                  : {}
              )}
            />
          )}
        </div>
      )
    }

    /*
     * Open order:
     * always allow another shipment.
     */
    return (
      <div className="flex justify-end gap-1">
        {shipments.length > 0 && (
          <OrderLabelsMenu
            orderId={
              row.original.order_id
            }
            shipments={
              shipments
            }
            onRefreshed={() =>
              void getAllOrderShipments.trigger(
                undefined,
                {
                  skipCache: true,
                }
              )
            }
            {...(
              trackingEmailEnabled
                ? {
                    onEmailTracking:
                      () =>
                        openTrackingEmail(
                          row.original
                            .order_id
                        ),
                  }
                : {}
            )}
          />
        )}

        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400"
          onClick={() =>
            setOrderToShip(
              row.original.order_id
            )
          }
        >
          <Package className="w-3 h-3 mr-1" />
          {shipments.length > 0
            ? 'Add Shipment'
            : 'Ship'}
        </Button>

        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-green-600 dark:hover:text-green-400"
          onClick={() =>
            setOrderToMarkShipped(
              row.original.order_id
            )
          }
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          Mark Shipped
        </Button>
      </div>
    )
  },
},
    ],
    [baseColumns, shipmentsByOrder, getAllOrderShipments, trackingEmailEnabled, openTrackingEmail]
  )

  const deliveryColumns = useMemo<ColumnDef<FulfillmentOrder>[]>(
    () => [
      {
        id: 'select',
        enableSorting: false,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={value => table.toggleAllRowsSelected(!!value)}
            onClick={e => e.stopPropagation()}
            aria-label="Select all awaiting delivery orders"
          />
        ),
        cell: ({ row }) =>
          row.getCanSelect() ? (
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={value => row.toggleSelected(!!value)}
              aria-label={`Select order ${row.original.order_id}`}
            />
          ) : null,
      },
      ...baseColumns,
      {
        id: 'method',
        header: 'Method',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">{methodLabel(row.original.delivery_method)}</span>
        ),
      },
      {
        id: 'deliveryStatus',
        header: 'Status',
        cell: ({ row }) =>
          row.original.status === 'delivered' ? (
            <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-950">
              Delivered
            </Badge>
          ) : (
            <Badge variant="outline">Awaiting</Badge>
          ),
      },
      {
        id: 'action',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.status === 'delivered') {
            return <div className="flex justify-end" />
          }
          return (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-green-600 dark:hover:text-green-400"
                onClick={() =>
                  setOrderToDeliver({ id: row.original.order_id, customer: row.original.customer })
                }
              >
                <Truck className="w-3 h-3 mr-1" />
                Mark Delivered
              </Button>
            </div>
          )
        },
      },
    ],
    [baseColumns]
  )

  return (
    <div style={{ padding: '8px 12px' }} className="flex flex-col gap-4 min-h-screen bg-background overflow-y-auto">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 border rounded-lg p-4 bg-card">
        {([
          { label: 'Shipping Orders', value: stats.shippingTotal },
          { label: 'Shipped', value: stats.shipped },
          { label: 'Awaiting Ship', value: stats.shippingAwaiting },
          { label: 'Delivery Orders', value: stats.deliveryTotal },
          { label: 'Delivered', value: stats.delivered },
          { label: 'Awaiting Delivery', value: stats.deliveryAwaiting },
        ] as const).map(s => (
          <div key={s.label} className="flex flex-col items-center p-3 rounded-md bg-background border">
            <span className="text-xs text-muted-foreground uppercase tracking-wide text-center">{s.label}</span>
            <span className="text-3xl font-bold text-foreground mt-1">{s.value}</span>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border rounded-lg p-4 bg-card flex flex-col gap-3">
        <Tabs key={initialTab} defaultValue={initialTab} className="w-full">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-xl font-semibold text-foreground">Fulfillment</h2>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch
                  id="show-completed"
                  checked={showCompleted}
                  onCheckedChange={setShowCompleted}
                />
                <Label htmlFor="show-completed" className="text-sm text-muted-foreground cursor-pointer">
                  Show shipped/delivered
                </Label>
              </div>
              <TabsList>
                <TabsTrigger value="shipping">
                  <Package className="w-4 h-4 mr-1" />
                  Shipping
                </TabsTrigger>
                <TabsTrigger value="delivery">
                  <Truck className="w-4 h-4 mr-1" />
                  Delivery
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          <TabsContent value="shipping" className="mt-4">
            <OrdersTable
              orders={shippingOrders}
              columns={shippingColumns}
              loading={getAllOrderLines.loading}
              filterValue={shippingFilter}
              onFilterChange={setShippingFilter}
              onRefresh={refreshAll}
              refreshing={getAllOrderLines.loading}
            />
          </TabsContent>

          <TabsContent value="delivery" className="mt-4">
            <div className="flex justify-end gap-2 flex-wrap mb-3">
              {selectedDeliveryOrders.length > 0 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeliverySelection({})}
                  >
                    Clear selection
                  </Button>
                  <Button
                    size="sm"
                    onClick={() =>
                      setBulkDeliverOrders(
                        selectedDeliveryOrders.map(o => ({ id: o.order_id, customer: o.customer }))
                      )
                    }
                  >
                    <Truck className="w-4 h-4 mr-1" />
                    Mark Delivered ({selectedDeliveryOrders.length})
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleGenerateDeliveryPdf()}
                disabled={deliveryPdfLoading}
              >
                {deliveryPdfLoading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-1" />
                )}
                Delivery Manifest
              </Button>
            </div>
            <OrdersTable
              orders={deliveryOrders}
              columns={deliveryColumns}
              loading={getAllOrderLines.loading}
              filterValue={deliveryFilter}
              onFilterChange={setDeliveryFilter}
              onRefresh={refreshAll}
              refreshing={getAllOrderLines.loading}
              rowSelection={deliverySelection}
              onRowSelectionChange={setDeliverySelection}
              canSelectRow={o => o.status !== 'delivered'}
            />
          </TabsContent>
        </Tabs>
      </div>

      <ShipOrderDialog
  orderId={orderToShip}
  onOpenChange={open => {
    if (!open) setOrderToShip(null)
  }}
  onShipped={handleOrderShipped}
  {...(
    trackingEmailEnabled
      ? { onEmailTracking: handleShipDialogEmailTracking }
      : {}
  )}
/>

      <TrackingEmailDialog
        orderId={emailOrderId}
        open={emailOpen}
        onOpenChange={open => {
          setEmailOpen(open)
          if (!open) setEmailOrderId(null)
        }}
      />

      <DeliverOrderDialog
        orderId={orderToDeliver?.id ?? null}
        customer={orderToDeliver?.customer ?? ''}
        onOpenChange={open => { if (!open) setOrderToDeliver(null) }}
        onDelivered={refreshAll}
      />

      <BulkDeliverDialog
        orders={bulkDeliverOrders}
        onOpenChange={open => { if (!open) setBulkDeliverOrders(null) }}
        onDone={handleBulkDelivered}
      />

      <AlertDialog
        open={orderToMarkShipped !== null}
        onOpenChange={open => { if (!open && !markingShipped) setOrderToMarkShipped(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark order {orderToMarkShipped} shipped?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks the order as shipped without purchasing a carrier label. Use this for orders
              fulfilled outside of Shippo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={markingShipped}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault()
                void handleMarkShipped()
              }}
              disabled={markingShipped}
            >
              {markingShipped ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
              Mark Shipped
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
